import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

// L4.04 — SLA / TAT matrix (view).
// GET /v1/wf/sla → { rows, total }; each row includes its step.

interface WfTat {
  readonly tatConfigId: string;
  readonly tatValue: number;
  readonly tatUnit: string;
  readonly warningThresholdPercent: number;
  readonly autoActionOnTimeout: string | null;
  readonly excludeHolidays: boolean;
  readonly excludeWeekends: boolean;
  readonly step?: { readonly stepName: string; readonly stepSequence: number };
}

export function WorkflowSlaPage(): ReactNode {
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<WfTat[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ rows: WfTat[]; total: number }>('/v1/wf/sla', { token })
      .then((r) => { if (alive) { setRows(r.rows); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="wf-sla">
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('wf.sla.title')}</h1>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="sla-empty" style={{ color: 'var(--color-text-muted)' }}>{t('wf.sla.empty')}</p>
      ) : (
        <DataTable<WfTat>
          ariaLabel="sla-table"
          rows={rows}
          rowKey={(row) => row.tatConfigId}
          rowTestId={(row) => `sla-row-${row.tatConfigId}`}
          columns={[
            { key: 'step', header: t('wf.sla.colStep'), render: (row) => (row.step ? `${row.step.stepSequence}. ${row.step.stepName}` : '—') },
            { key: 'tat', header: t('wf.common.tat'), render: (row) => `${row.tatValue} ${row.tatUnit}` },
            { key: 'warnPct', header: t('wf.common.warnPct'), render: (row) => `${number(row.warningThresholdPercent)}%` },
            { key: 'onTimeout', header: t('wf.common.onTimeout'), render: (row) => row.autoActionOnTimeout ?? '—' },
            { key: 'exclHolidays', header: t('wf.sla.colExclHolidays'), render: (row) => (row.excludeHolidays ? t('wf.common.yesLower') : t('wf.common.noLower')) },
            { key: 'exclWeekends', header: t('wf.sla.colExclWeekends'), render: (row) => (row.excludeWeekends ? t('wf.common.yesLower') : t('wf.common.noLower')) },
          ]}
        />
      )}
    </main>
  );
}
